import { useEffect, useRef, useState } from 'react'
import type { Game, AvailableActionOption } from '../../types/game'
import { HexCell } from './HexCell'
import { NestingModal } from './NestingModal'
import { assetUrl } from '../../utils/assets'

interface HiveBoardProps {
  game: Game
  seeds: number[]
  availableActions: AvailableActionOption[]
  isMyTurn?: boolean
  onBuild: (cellId: string) => void
  onMakeHoney: (cellId: string) => void
  onLayEgg: (cellId: string) => void
  onClearInfection: (cellId: string) => void
  scale?: number
  draggable?: boolean
}

export function HiveBoard({
  game,
  seeds,
  availableActions,
  isMyTurn = true,
  onBuild,
  onMakeHoney,
  onLayEgg,
  onClearInfection,
  scale = 1,
  draggable = false,
}: HiveBoardProps) {
  const [activeNestingCellId, setActiveNestingCellId] = useState<string | null>(null)
  const boardCameraRef = useRef<HTMLDivElement>(null)
  const panRef = useRef({ x: 0, y: 0 })
  const dragRef = useRef<{ startX: number; startY: number; startPanX: number; startPanY: number; moved: boolean } | null>(null)
  const hasDraggedRef = useRef(false)
  const cells = game.hive.cells

  // Keep --mobile-board-scale in sync with the scale prop
  useEffect(() => {
    if (draggable) {
      boardCameraRef.current?.style.setProperty('--mobile-board-scale', String(scale))
    }
  }, [scale, draggable])

  const applyPan = () => {
    boardCameraRef.current?.style.setProperty('--mobile-board-pan-x', `${panRef.current.x}px`)
    boardCameraRef.current?.style.setProperty('--mobile-board-pan-y', `${panRef.current.y}px`)
  }

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!draggable) return
    dragRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      startPanX: panRef.current.x,
      startPanY: panRef.current.y,
      moved: false,
    }
    hasDraggedRef.current = false
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current
    if (!drag) return
    const dx = event.clientX - drag.startX
    const dy = event.clientY - drag.startY
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) {
      drag.moved = true
      hasDraggedRef.current = true
    }
    panRef.current = { x: drag.startPanX + dx, y: drag.startPanY + dy }
    applyPan()
  }

  const handlePointerUp = () => {
    dragRef.current = null
    // Clear the drag flag after a tick so the click handler can check it first
    setTimeout(() => { hasDraggedRef.current = false }, 0)
  }

  // Swallow clicks that are actually the end of a drag
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (hasDraggedRef.current) {
      event.stopPropagation()
    }
  }

  useEffect(() => {
    if (!activeNestingCellId) {
      return
    }
    const activeCell = cells.find((cell) => cell.id === activeNestingCellId)
    const broodChamberIds = new Set((game.hive.brood_chambers ?? []).map((broodCell) => broodCell.id))
    const isBroodChamber = activeCell ? broodChamberIds.has(activeCell.id) : false
    const eggColor = game.hive_eggs[activeNestingCellId]
    const honey = game.hive_honey[activeNestingCellId] ?? []
    if (!isBroodChamber || eggColor || honey.length > 0) {
      setActiveNestingCellId(null)
    }
  }, [activeNestingCellId, cells, game.hive.brood_chambers, game.hive_eggs, game.hive_honey])

  return (
    <div
      className="board"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onClick={handleClick}
    >
      <div
        ref={boardCameraRef}
        className="board-camera"
        style={draggable || scale === 1 ? undefined : { transform: `scale(${scale})`, transformOrigin: 'center center' }}
      >
        <img
          src={assetUrl('images', 'seasons', `board_${game.current_season}.png`)}
          alt="hive board"
          className="backdrop"
        />
        <div className="hive">
          <div className="honey-comb">
            {cells.map((cell) => {
              const row = (cell.x + 2) * 3 + 4
              // absolute position within the full-size row, so the trimmed
              // 2/3-player boards keep every cell in its 4-player spot
              const index = cell.y - Math.max(-3, -cell.x - 3)
              const column = Math.abs(cell.x) + index * 2 + 1

              return (
                <HexCell
                  key={cell.id}
                  cell={cell}
                  game={game}
                  seed={seeds[index]}
                  gridRowStart={row}
                  gridColumnStart={column}
                  availableActions={[]}
                  onActivateNesting={(cellId) => {
                    if (!isMyTurn) return
                    setActiveNestingCellId((current) => (current === cellId ? null : cellId))
                  }}
                  onBuild={onBuild}
                  onMakeHoney={(cellId) => {
                    setActiveNestingCellId(null)
                    onMakeHoney(cellId)
                  }}
                  onLayEgg={(cellId) => {
                    setActiveNestingCellId(null)
                    onLayEgg(cellId)
                  }}
                  onClearInfection={onClearInfection}
                />
              )
            })}
          </div>
        </div>
      </div>
      {activeNestingCellId && isMyTurn && (
        <NestingModal
          canMakeHoney={true}
          canLayEgg={true}
          playerColor={game.current_player.color}
          onMakeHoney={() => {
            setActiveNestingCellId(null)
            onMakeHoney(activeNestingCellId)
          }}
          onLayEgg={() => {
            setActiveNestingCellId(null)
            onLayEgg(activeNestingCellId)
          }}
          onCancel={() => setActiveNestingCellId(null)}
        />
      )}
    </div>
  )
}
